// Das Hilfe-Register (Auftrag 8, R1). Ein Begriff, ein Text -- überall
// derselbe, auf der Mieterseite, im Kontoauszug und in der Wache.
//
// DIE REGEL ÜBER ALLEM: Hilfe erklärt die Festlegung, nicht die Oberfläche.
// Nicht „hier Datum eingeben", sondern was das Datum im System bedeutet.
// Jeder Text ist ABGESCHRIEBEN aus einem Code-Kommentar, einem Auftrag oder
// einem Votum, und `quelle` nennt die Fundstelle (Pfad ab Repo-Wurzel, mit
// :zeile oder :von-bis). Ein Text ohne Quelle gehört nicht hierher: Die Hilfe
// ist genau dann schädlich, wenn sie etwas behauptet, das die App nicht tut.
// Fehlt zu einem Begriff die Quelle, wird der Eintrag nicht erfunden, sondern
// als Entscheidungsmemo vorgelegt.
//
// WAS NICHT HIERHER GEHÖRT -- die Trennung, an der alles hängt:
//   Schicht 1 (Begriff) und Schicht 4 (Seite) stehen im Register.
//   Schicht 2 (Zustand: Ampel, Status, Befund) bekommt KEINEN Eintrag. Dort
//   ist der `grund` / die `klaerung` aus der Backend-Antwort der Text. Ein
//   zweiter, danebenstehender Satz veraltet genau dann, wenn jemand bewerte()
//   ändert. Was eine SPALTE oder ÜBERSCHRIFT bedeutet, ist Schicht 1 und darf
//   hierher; was ein einzelner WERT gerade bedeutet, nie.
//
// Einträge mit id "seite.<name>" sind Seitenköpfe (<Seitenkopf id>): `text`
// ist dann {frage, tun, nicht} -- welche Frage die Seite beantwortet, was man
// hier tut, was nicht. Alle anderen sind Begriffe (<Hilfe id>) mit Text als
// Zeichenkette.
//
// Dieses Modul importiert nichts: scripts/hilfe_pruefen.mjs lädt es vor dem
// Bau unverändert in Node.

const EINTRAEGE = [
    {
        id: "mietende",
        text: "Das Mietende ist der letzte Miettag, meist ein Monatsletzter, und beendet das Soll. "
            + "Die Übergabe (Schlüsselrückgabe) ist ein zweites Datum, fällt selten auf denselben "
            + "Tag und beendet das Soll nicht.",
        mehr: "Festlegung vom 05.10.2026. Für jedes laufende Mietverhältnis braucht es das "
            + "Mietende; die Übergabe ist optional. Das System erfindet kein Datum.",
        quelle: [
            "frontend/src/pages/Tenants.jsx:535-538",
            "backend/server.py:1326-1328",
            "fahrplan/voten/2026-11-03_hilfe-systematik.md:47-48",
        ],
    },
    {
        id: "arbeitsliste",
        text: "Die Liste rechnet nichts selbst: Sie liest die Ampeln der Module, und keine Zeile "
            + "verlangt eine neue Eingabe. Leer heißt „nichts zu tun“, nicht „niemand hat "
            + "eingetragen“ – deshalb zeigt sie „nichts offen“ nur, wenn alle Quellen geantwortet haben.",
        quelle: "frontend/src/pages/Dashboard.jsx:417-425",
    },

    // ---------------------------------------------------- Seitenköpfe (R5)
    // Je Seite: welche Frage sie beantwortet, was man hier tut, was nicht.
    // Seiten ohne belegte Quelle haben (noch) keinen Kopf -- sie stehen im
    // Entscheidungsmemo des Berichts zu Auftrag 8, Schritt B.
    {
        id: "seite.aufgaben",
        text: {
            frage: "Was steht insgesamt an? Aufgaben liegen an der Einheit – hier stehen alle "
                + "Aufgaben über alle Einheiten in einer Tabelle.",
            tun: "Nach jeder Spalte sortieren: Je Aufgabe stehen Entität, Immobilie, Einheit und "
                + "aktueller Mieter dabei.",
            nicht: "Die Seite entscheidet nicht fachlich vor, was wichtiger ist. Sie ordnet nur stabil "
                + "(überfällig zuerst, dann Fälligkeit) – welche Spalte zählt, entscheidest du.",
        },
        quelle: ["backend/server.py:1689-1699", "frontend/src/pages/Aufgaben.jsx:18-20"],
    },
    {
        id: "seite.dashboard",
        text: {
            frage: "Was steht an? Die Arbeitsliste zeigt es nach Dringlichkeit, jede Zeile mit Sprungziel.",
            tun: "Kennzahlen und Zeilen anklicken: Sie sind Einstiege und führen auf die passend "
                + "gefilterte Liste.",
            nicht: "Die Startseite rechnet nichts selbst und verlangt keine neue Eingabe – sie liest "
                + "die Ampeln der Module.",
        },
        quelle: ["backend/server.py:1571-1575", "frontend/src/pages/Dashboard.jsx:24-26"],
    },
    {
        id: "seite.fehlende_mieter",
        text: {
            frage: "Welche regelmäßigen Zahler haben im System kein Mietverhältnis? Bei ihnen fehlt "
                + "nicht die Zuordnung, sondern das Soll.",
            tun: "Vorschläge prüfen – mit Einheit, Betrag und frühestem Monat –, bestätigen und die "
                + "Vertragsdaten ergänzen.",
            nicht: "Angelegt wird erst, wenn ein Mensch bestätigt. Inhaber, Entitäten und Verwaltungen "
                + "werden nicht als Mieter vorgeschlagen.",
        },
        quelle: ["frontend/src/pages/FehlendeMieter.jsx:16-20", "backend/mietkonto.py:2825-2830"],
    },
    {
        id: "seite.historie",
        text: {
            frage: "Wer hat wann was geändert, und was stand vorher da?",
            tun: "Einträge mit Kontoauszügen und Mails vergleichen – deshalb mit Datum und Uhrzeit, "
                + "nicht „vor 3 Minuten“.",
            nicht: "Die Werte werden lesbar gemacht, aber nicht gedeutet: Zahlen bleiben Zahlen, "
                + "denn nicht jedes Feld ist Geld.",
        },
        quelle: ["backend/historie.py:1", "frontend/src/pages/Historie.jsx:24-25",
            "frontend/src/pages/Historie.jsx:35-37"],
    },
    {
        id: "seite.konten",
        text: {
            frage: "Welche Konten kommen aus den eingelesenen Auszügen, und wie weit sind sie zugeordnet?",
            tun: "Über die Reiter filtern: „Mieten“ ist die Liste, aus der Mieteingänge zugeordnet "
                + "werden; jede andere Kontoart bleibt über die Auswahl erreichbar.",
            nicht: "Darlehens- und Privatkonten bleiben im Bestand, stehen aber nicht in der Liste, "
                + "aus der man Mieten zuordnet. „unklar“ ist kein Fehler: Dort liegen Entitäts- und "
                + "Sammelkonten, die nichts in der Mietzuordnung zu suchen haben.",
        },
        quelle: ["backend/mietkonto.py:4072-4077", "frontend/src/pages/Konten.jsx:795-798",
            "frontend/src/pages/Konten.jsx:30-33", "frontend/src/pages/Konten.jsx:46-48"],
    },
    {
        id: "seite.liegenschaften",
        text: {
            frage: "Welche Objekte gehören zu einem Haus? Die Liegenschaft ist die Klammer über den "
                + "Objekten – Teilungserklärung, Beschlusssammlung, Wirtschaftsplan und "
                + "Verwaltervertrag gelten einmal für alle.",
            tun: "Liegenschaften anlegen: Der Trockenlauf zeigt, was entstehen würde; geschrieben "
                + "wird erst auf ausdrückliche Bestätigung.",
            nicht: "Die Dokumente werden nicht hier angezeigt, sondern in Drive geöffnet – dort "
                + "liegen sie, dort gelten die Rechte.",
        },
        quelle: ["backend/liegenschaft.py:2-10", "frontend/src/pages/Liegenschaften.jsx:218-219",
            "frontend/src/components/Dokumente.jsx:70-71"],
    },
    {
        id: "seite.mietkonto",
        text: {
            frage: "Soll gegen Ist: Ist die Miete da – und wo fehlt das meiste Geld?",
            tun: "Zahlungen zuordnen und Abweichungen klären: Eine erklärte Abweichung verschwindet "
                + "aus der Arbeitsliste, ohne dass man sie vergisst.",
            nicht: "Ein Vorschlag ist keine Zahlung: Gezählt wird nur bestätigt Zugeordnetes.",
        },
        quelle: ["backend/mietkonto.py:2-6", "frontend/src/pages/Mietkonto.jsx:48-49",
            "frontend/src/pages/Mietkonto.jsx:261-262", "backend/mietkonto.py:3757-3758"],
    },
    {
        id: "seite.mieter",
        text: {
            frage: "Wo stehen die Daten eines Mieters? Eine eigene Mieter-Detailseite gibt es nicht – "
                + "der Dialog ist die Detailansicht.",
            tun: "Im Dialog die Personen eines Vertrags als Zeilen führen: Ein gemeinsamer "
                + "Mietvertrag bleibt ein Mietverhältnis.",
            nicht: "Gespeicherte Personen werden nicht gelöscht – sie scheiden über „ausgeschieden "
                + "am“ aus, das ist Vertragsgeschichte.",
        },
        quelle: ["frontend/src/pages/Tenants.jsx:304-307", "frontend/src/pages/Tenants.jsx:40-41",
            "frontend/src/pages/Tenants.jsx:65-67"],
    },
    {
        id: "seite.einheit",
        text: {
            frage: "Wer wohnt hier, was zahlt er, und was ist daran zu tun?",
            tun: "Den Betrag der Vertragsmiete korrigieren – für eine Erhöhung ab einem Stichtag ist "
                + "„Miete anpassen“ der richtige Weg.",
            nicht: "Besitz- und Eigentumsdaten stehen am Objekt: Die Einheit erbt sie und zeigt sie "
                + "hier nicht doppelt.",
        },
        quelle: ["frontend/src/pages/UnitDetail.jsx:176", "frontend/src/pages/UnitDetail.jsx:46-49",
            "frontend/src/pages/UnitDetail.jsx:377-378"],
    },
    {
        id: "seite.widersprueche",
        text: {
            frage: "Was sagt der Bestand über sich selbst Widersprüchliches? Diese Widersprüche "
                + "kosten Geld, nicht Schönheit.",
            tun: "Jede Zeile führt dorthin, wo sich der Widerspruch auflösen lässt – die Ansicht ist "
                + "eine Arbeitsliste, kein Bericht.",
            nicht: "Die Wache pflegt nicht selbst, sie führt hin. Ein Widerspruch verschwindet erst, "
                + "wenn die Daten stimmen, nicht wenn jemand abhakt.",
        },
        quelle: ["frontend/src/pages/Widersprueche.jsx:14-16", "backend/mietkonto.py:3170-3171",
            "fahrplan/FAHRPLAN.md:421", "fahrplan/voten/2026-10-09_fuenf-punkte-reihenfolge.md:258-260"],
    },
    {
        id: "seite.zugaenge",
        text: {
            frage: "Wer sieht hinein? Die Zugänge sind für jede Rolle lesbar – wer mit anderen in "
                + "denselben Daten arbeitet, soll wissen, wer sonst hineinsieht.",
            tun: "Rolle und Bereich vergeben: lesen, schreiben (der Alltag), verwalten (zusätzlich "
                + "löschen und Zugänge verwalten), admin (unbeschränkt). Eine Entität schließt alle "
                + "ihre Häuser ein, auch künftige.",
            nicht: "Rechte einzeln zusammenklicken gibt es nicht, und ein Verwalter kann nur "
                + "weitergeben, was er selbst sieht.",
        },
        quelle: ["backend/auth.py:490-491", "backend/auth.py:24-34", "frontend/src/pages/Zugaenge.jsx:46-48",
            "frontend/src/pages/Zugaenge.jsx:337-339"],
    },
    {
        id: "seite.wiki",
        text: {
            frage: "Was gilt für alle? Hier stehen die globalen Einträge – Hausordnung, "
                + "Handwerkerliste –, die jeder sieht; Einträge zu einem Haus stehen an der Immobilie.",
            tun: "Einträge diktieren: Die Sprachnotiz wird transkribiert und strukturiert.",
            nicht: "Ein Diktat speichert nicht von selbst – es liefert eine Vorschau, die du "
                + "bestätigst oder anpasst.",
        },
        quelle: ["backend/wiki_service.py:96-99", "backend/wiki_service.py:2",
            "backend/wiki_service.py:608-610"],
    },
    {
        id: "seite.durchsicht",
        text: {
            frage: "Welche vorgeschlagenen Zuordnungen gelten? Erst eine bestätigte Zuordnung zählt "
                + "gegen das Soll – der Vorschlag nimmt die Arbeit ab, nicht die Entscheidung.",
            tun: "Vorschläge bestätigen oder verwerfen. Zwei Bündelungen nehmen die Masse: "
                + "gleichförmige Jahre je Mieter (jede Periode genau das Soll, immer derselbe "
                + "Einzahler) und sichere Einzelne (Sicherheit ≥ 0,9, höchstens sieben Tage von der "
                + "Fälligkeit, nur auf Einheitenkonten).",
            nicht: "Was in keine Bündelung fällt, wird nicht pauschal entschieden: Es bleibt "
                + "Einzelfall mit seinem Grund.",
        },
        quelle: [
            "backend/mietkonto.py:2077-2080",
            "frontend/src/pages/Durchsicht.jsx:51-58",
        ],
    },

    // ---------------------------------------------------- Wirkungen (R3)
    // Was ein Knopf auslöst. Die Quelle ist die Funktion, die er aufruft --
    // Docstring der Backend-Route oder Kommentar in lib/api.js. id =
    // aktion.<apiFunktion>, damit dieselbe Aktion überall denselben Text hat.
    {
        id: "aktion.allocationsBestaetigen",
        text: "Vorschläge bestätigen. Jeder Lauf bekommt eine Lauf-Kennung an jede Zuordnung, damit er als Ganzes zurückgenommen werden kann.",
        quelle: ["backend/mietkonto.py:1883-1888", "frontend/src/lib/api.js:191-192"],
    },
    {
        id: "aktion.confirmAllocation",
        text: "Erst die Bestätigung wirkt auf die Ampel: Sie ist die buchhalterische Aussage „diese Zahlung deckt diese Miete“. Ein Vorschlag zählt bis dahin nicht gegen das Soll.",
        quelle: ["backend/mietkonto.py:1598-1600", "backend/mietkonto.py:2077-2080"],
    },
    {
        id: "aktion.createMietanpassung",
        text: "Eine Anpassung oder eine ganze Staffel anlegen. Eine Anpassung sagt: ab diesem Monat gilt dieser Betrag – die Soll-Erzeugung fragt danach, statt den Betrag am Mietverhältnis zu nehmen. Danach gleicht die Nachberechnung schon erzeugte Soll-Positionen an eine rückwirkende Anpassung an.",
        quelle: ["backend/mietanpassung.py:761", "frontend/src/lib/api.js:414-415", "backend/mietanpassung.py:609"],
    },
    {
        id: "aktion.deleteMietanpassung",
        text: "Löscht die Anpassung. Zurückziehen ist der bessere Weg: Eine gelöschte Anpassung nimmt der Miete ihre Geschichte. Löschen bleibt für Datensätze, die aus Versehen entstanden sind.",
        quelle: "backend/mietanpassung.py:847-853",
    },
    {
        id: "aktion.fehlendeMietverhaeltnisse",
        text: "Regelmäßige Zahler ohne Mietverhältnis – als Vorschläge für neue Mietverhältnisse, mit Einheit, Betrag und frühestem Monat. Nur lesend.",
        quelle: "backend/mietkonto.py:2825-2826",
    },
    {
        id: "aktion.importBestand",
        text: "Bestandsimport per Excel (pro Entität) mit drei Tabellenblättern: Wirtschaftseinheiten, Mieteinheiten, Mieter. Die Excel-Datei wird hochgeladen und validiert, dann wird angelegt.",
        quelle: "backend/import_bestand.py:2-12",
    },
    {
        id: "aktion.liegenschaftNamensvorschlag",
        text: "Schlägt die Bezeichnung nach der Regel des Auftraggebers vor: Mehrere Adressen derselben Straße werden zusammengezogen („Sophienstr. 4“ + „Sophienstr. 6“ → „Sophienstr. 4-6“), bei verschiedenen Straßen bleibt es bei einer Aufzählung. Ist die Liegenschaft nicht ungeteilt, steht „WEG“ davor.",
        quelle: ["backend/liegenschaft.py:87-91", "backend/liegenschaft.py:107"],
    },
    {
        id: "aktion.mietanpassungNachziehen",
        text: "Wirksam gewordene Mietanpassungen in die Mietverhältnisse schreiben. Täglich läuft dasselbe aus dem Cron.",
        quelle: "frontend/src/lib/api.js:339-340",
    },
    {
        id: "aktion.mieterKonto",
        text: "Alle Forderungen eines Mietverhältnisses und was darauf gezahlt wurde. Nur lesend.",
        quelle: ["backend/mietkonto.py:922-934", "frontend/src/lib/api.js:343-344"],
    },
    {
        id: "aktion.sammelZuruecknehmen",
        text: "Eine Sammelbestätigung als Ganzes zurücknehmen – zurück auf Vorschlag. Gelöscht wird nichts: Die Zuordnungen bleiben, sie zählen nur nicht mehr gegen das Soll.",
        quelle: "backend/mietkonto.py:1953-1956",
    },
    {
        id: "aktion.setUserPassword",
        text: "Setzt ein neues Passwort. Beim eigenen Zugang ist das alte Pflicht; beim Zugang eines anderen gilt der Fall „vergessen“: kein Zufallspasswort – wer verwaltet, tippt ein Passwort ein und sagt es persönlich.",
        quelle: ["backend/auth.py:438", "backend/auth.py:584-588"],
    },
    {
        id: "aktion.updateMietanpassung.zurueckziehen",
        text: "Setzt den Status „zurückgezogen“: vom Vermieter zurückgenommen – zählt nicht. Zurückziehen ist der bessere Weg als Löschen.",
        quelle: ["backend/mietanpassung.py:25", "backend/mietanpassung.py:850-851"],
    },
    {
        id: "aktion.updateMietanpassung.zustimmung",
        text: "Trägt die Zustimmung des Mieters ein. Eine Zustimmung macht die Anpassung wirksam.",
        quelle: "backend/mietanpassung.py:829-832",
    },
    {
        id: "aktion.updateUser.aktiv",
        text: "Legt den Zugang still oder aktiviert ihn wieder. Stilllegen wirkt sofort: Der Zugang wird bei jeder Anfrage geprüft, nicht erst, wenn sein Token abläuft.",
        quelle: ["backend/auth.py:151-153", "backend/auth.py:526"],
    },
    {
        id: "aktion.zahlungKeineMiete",
        text: "Kennzeichnet einen Eingang als „gehört nicht zur Miete“ – oder nimmt das zurück. Der Haken löscht nichts: Die Zahlung bleibt im Bestand, sie zählt nur nicht mehr als offener Mieteingang und wird nicht mehr vorgeschlagen.",
        quelle: ["backend/mietkonto.py:1296-1302", "frontend/src/lib/api.js:379-380"],
    },

    // ---------------------------------------------------- Feldhilfen (R4)
    // Begriffe an Feldern und Spalten der Dialoge Mieter, Einheit, Immobilie,
    // Zahlung zuordnen, Mietende und Kontoauszug.
    {
        id: "abrechnung-richtung",
        text: "Das Vorzeichen trägt die Richtung: Eine Nachzahlung des Mieters ist positiv, ein Guthaben zu seinen Gunsten negativ. Ein Guthaben ist eine Verbindlichkeit, keine Forderung, und erst erledigt, wenn die Auszahlung zugeordnet ist.",
        quelle: ["backend/mietkonto.py:275-277", "backend/mietkonto.py:599-602"],
    },
    {
        id: "adresse-gruppierung",
        text: "Über die Adresse schlägt die App vor, welche Objekte zu einer Liegenschaft gehören – mehr gibt der Bestand nicht her. Dass zwei Adressen eine WEG sind, weiß nur der Auftraggeber; solche Zusammenlegungen bleiben Handarbeit.",
        quelle: ["backend/liegenschaft.py:271-276"],
    },
    {
        id: "banking4-export",
        text: "Eingelesen wird derzeit der Banking4-Tabellenexport („Tabelle als CSV exportieren“); er enthält alle Konten in einer Datei. Trockenlauf als Standard: Zuerst wird nur geprüft.",
        quelle: ["backend/bank_import.py:2-8", "backend/mietkonto.py:3848-3850"],
    },
    {
        id: "besitz-ab",
        text: "Besitzübergang beim Kauf, der Nutzen-Lasten-Wechsel: ab wann die Mieten diesem Bestand zustehen – eine Fälligkeit zählt ins Soll, wenn sie am oder nach dem Übergang liegt; ohne Datum gilt der Bestand als immer vorhanden. Bewusst nicht das Kaufdatum: Der Kaufvertrag liegt oft Monate früher, und bis zum Übergang vereinnahmt der Verkäufer die Mieten. Die Einheiten erben das Datum des Objekts und tragen kein eigenes.",
        quelle: ["backend/server.py:514-519", "backend/mietkonto.py:417-424", "fahrplan/FAHRPLAN.md:356", "fahrplan/FAHRPLAN.md:383"],
    },
    {
        id: "besitz-bis",
        text: "Besitzübergang beim Verkauf: Ab diesem Tag gehört die Miete dem Käufer. Eine Fälligkeit zählt nur, wenn sie vor dem Übergang liegt – der Monat, dessen Dritter noch vor dem Übergang liegt, ist unserer; dieselbe Kante wie beim Kauf. Die Einheiten erben das Datum des Objekts, nie umgekehrt.",
        quelle: ["backend/mietkonto.py:430-434", "backend/server.py:521-523", "fahrplan/FAHRPLAN.md:380"],
    },
    {
        id: "buchungen-erkannt",
        text: "Eingelesen werden alle Bewegungen, auch Belastungen. Nur Eingänge sind einer Soll-Position zuordenbar, aber ein Konto, das nur die Hälfte seiner Bewegungen kennt, lässt sich nicht prüfen.",
        quelle: "backend/bank_import.py:28-30",
    },
    {
        id: "differenz",
        text: "Das Delta eines Monats ist, was auf ihn zugeordnet ist – auch noch nicht Bestätigtes –, minus das Soll: negativ heißt zu wenig gekommen, positiv zu viel, 0,00 heißt „stimmt“. Es bleibt stehen, wo es entstanden ist, und wird nicht in den nächsten Monat gerollt. Die Differenz ist die Summe der Monatsdeltas – negativ heißt: unterm Strich fehlt Geld.",
        quelle: ["backend/mietkonto.py:977-982", "backend/mietkonto.py:1078-1079", "frontend/src/components/MieterKontoDialog.jsx:258-260"],
    },
    {
        id: "eigenes-grundbuch",
        text: "Eigenes Grundbuch heißt Sondereigentum; ohne eigenes Grundbuch ist die Einheit Zubehör oder Sondernutzungsrecht. Bei Wohnung oder Gewerbe ohne eigenes Grundbuch ist das Grundbuch der Immobilie maßgeblich.",
        quelle: ["backend/server.py:653", "frontend/src/pages/Units.jsx:524"],
    },
    {
        id: "eigentumsumschreibung",
        text: "Die Eigentumsumschreibung im Grundbuch, bei Kauf und bei Verkauf, ist das rechtliche Datum und wirkt nicht ins Soll – das steuern die Besitzübergänge. Sie steht eigens da, damit Kaufvertrag, Besitz und Grundbuch nicht in einem Feld verschwimmen: vier Daten, vier Wirkungen, nie vermischt.",
        quelle: ["backend/server.py:521-526", "fahrplan/FAHRPLAN.md:88-91"],
    },
    {
        id: "einheit-iban",
        text: "Eine eigene IBAN überschreibt die der Immobilie und der Entität; vererbt wird Einheit > Immobilie > Entität. Die Konto-IBAN ist der Schlüssel zur Zuordnung.",
        quelle: ["backend/server.py:660", "backend/mietkonto.py:363-366"],
    },
    {
        id: "einheit-immobilie",
        text: "Das Objekt ist die Erwerbseinheit: Besitz- und Eigentumsdaten trägt nur das Objekt, die Einheit erbt sie und trägt nichts Eigenes.",
        quelle: ["backend/server.py:661-662", "fahrplan/FAHRPLAN.md:383"],
    },
    {
        id: "einheit-status",
        text: "„Unbekannt“ ist kein fehlender Wert, sondern eine Aussage: Zu dieser Einheit fehlen die Mieterdaten – nur so lässt sich bestätigter Leerstand von einer Datenlücke unterscheiden. Wird eine Einheit mit laufenden Mietverhältnissen auf „leer“ gesetzt, braucht jedes sein Mietende; das System nimmt nicht das Tagesdatum.",
        quelle: ["frontend/src/lib/format.js:85-87", "frontend/src/pages/Units.jsx:268-271"],
    },
    {
        id: "einheit-typ",
        text: "Wohnung und Gewerbe sind Haupteinheiten. Ein Zimmer ist die dritte Kategorie neben Haupteinheit und Zubehör: Es hängt wie Zubehör an einer Wohnung, trägt aber einen eigenen Mietvertrag.",
        quelle: ["frontend/src/pages/Units.jsx:47-48", "frontend/src/pages/Units.jsx:262-263"],
    },
    {
        id: "einzugsdatum",
        text: "Beginnt das Mietverhältnis im Monatsverlauf, ist die Miete dieses Monats anteilig – nach Kalendertagen, weil der Mietvertrag Monate und keine Werktage kennt. Der Einzug wird dabei spiegelbildlich zum Mietende behandelt (Annahme, Memo O9).",
        quelle: ["backend/server.py:251-254", "backend/mietkonto.py:811-812"],
    },
    {
        id: "erwerbseinheit",
        text: "Das Objekt ist die Erwerbseinheit: ein Kaufvertrag, eine Entität, ein Besitzübergang. Ein Objekt trägt nur dann Übergangsdaten, wenn es als Ganzes einer Entität gehört.",
        quelle: ["fahrplan/FAHRPLAN.md:383", "fahrplan/FAHRPLAN.md:380"],
    },
    {
        id: "faellig-am",
        text: "Die Fälligkeit ist ohne Angabe der dritte des Monats: Die Miete ist zu Beginn des Monats fällig, spätestens am dritten Werktag. Der Wert steht im Datensatz und ist pro Position änderbar; noch nicht fällige Positionen sind kein Rückstand.",
        quelle: ["backend/mietkonto.py:401-406", "backend/mietkonto.py:1068"],
    },
    {
        id: "faelligkeitsmonat",
        text: "Der Monat einer Abrechnungsposition ist der Monat der Fälligkeit, nicht der abgerechnete Zeitraum – der steht im Abrechnungsjahr.",
        quelle: "backend/mietkonto.py:274-275",
    },
    {
        id: "geplant",
        text: "Geplant heißt: angekündigt, noch nicht wirksam – die Anpassung zählt nicht gegen das Soll. Eine Erhöhung auf die ortsübliche Vergleichsmiete beginnt als geplant und wird erst wirksam, wenn der Mieter zustimmt oder die Frist ohne Widerspruch verstreicht.",
        quelle: ["backend/mietanpassung.py:21-22", "backend/mietanpassung.py:28-30"],
    },
    {
        id: "grundbuchblatt",
        text: "Aus dem Grundbuch-Eintrag zählt in dieser Spalte nur das Blatt. Gefüllt heißt: eigenes Grundbuch; leer heißt: keines. Ein Strich heißt: eigenes Grundbuch, aber kein Blatt erfasst.",
        quelle: ["frontend/src/pages/Units.jsx:725-727", "frontend/src/pages/Units.jsx:849-853", "frontend/src/lib/format.js:167-168"],
    },
    {
        id: "iban-vererbung",
        text: "Eigene IBAN für Mieteingänge dieser Immobilie; sie überschreibt die IBAN der Entität. Es gilt die Vererbung Einheit > Immobilie > Entität, und die Konto-IBAN ist der Schlüssel zur Zuordnung.",
        quelle: ["backend/server.py:513", "backend/mietkonto.py:361-364"],
    },
    {
        id: "kaltmiete-heute",
        text: "Die heute gültige Miete kommt aus den Mietanpassungen. Miete und Vorauszahlung stehen am Mietverhältnis, nicht an der Einheit: Ohne Mieter bleiben beide leer.",
        quelle: ["frontend/src/pages/Units.jsx:99", "frontend/src/pages/Units.jsx:882-883"],
    },
    {
        id: "konten-im-auszug",
        text: "Die eigene Konto-IBAN steht in jeder Zeile und ist der Schlüssel zur späteren automatischen Zuordnung – im Bestand gibt es ein Konto pro Mieteinheit. Die Konten werden auch im Trockenlauf festgehalten: Sie sind Bestandswissen, und der Zuordnungsvorschlag braucht sie; eine bereits bestätigte Zuordnung bleibt unangetastet.",
        quelle: ["backend/bank_import.py:7-8", "backend/bank_import.py:13-15", "backend/mietkonto.py:3889-3892"],
    },
    {
        id: "leer-seit",
        text: "Ohne Datum ist „leer“ nur eine Behauptung: Man weiß nicht, ob seit einer Woche oder seit zwei Jahren, und kann keinen Mietausfall rechnen. Beim Auszug des letzten Mieters wird es automatisch gesetzt – auf den Tag nach dem spätesten Mietende.",
        quelle: ["backend/server.py:640-642", "backend/server.py:370-371"],
    },
    {
        id: "liegenschaft",
        text: "Die Liegenschaft ist die Klammer über den Objekten, das eigentümerneutrale Haus: Teilungserklärung, Beschlusssammlung, Wirtschaftsplan und Verwaltervertrag gelten einmal für alle Objekte des Hauses, nicht für die einzelne Wohnung.",
        quelle: ["backend/liegenschaft.py:2-10", "backend/server.py:532-534", "fahrplan/FAHRPLAN.md:383"],
    },
    {
        id: "mietart",
        text: "Die Mietart bestimmt, woraus das Soll besteht: Kalt + NK + Heizung, oder die Pauschale. Bei Pauschalmiete gibt es keine getrennte Vorauszahlung – sie steckt drin.",
        quelle: ["frontend/src/pages/UnitDetail.jsx:37-38", "frontend/src/pages/Units.jsx:883-884"],
    },
    {
        id: "mietbetrag",
        text: "Der Betrag am Mietverhältnis. Für eine Erhöhung ab einem Stichtag ist eine Mietanpassung der richtige Weg – sonst bleibt der Verlauf leer. Schon erzeugte Soll-Positionen sind eingefroren.",
        quelle: ["frontend/src/pages/UnitDetail.jsx:46-49", "backend/mietanpassung.py:10-11"],
    },
    {
        id: "miete-gesamt",
        text: "Die Monatsmiete, die heute gilt: Kalt + NK + Heizung, oder die Pauschale. Welcher Betrag heute gilt, sagt der Verlauf – die Soll-Erzeugung fragt, welche Miete in diesem Monat galt, eine wirksame Mietanpassung eingeschlossen.",
        quelle: ["frontend/src/pages/UnitDetail.jsx:37-38", "frontend/src/pages/UnitDetail.jsx:307", "backend/mietanpassung.py:17-19"],
    },
    {
        id: "mieter-einheit",
        text: "Die Einheit, an der das Mietverhältnis hängt. An einer zimmerweise vermieteten Wohnung hängt kein eigener Mieter, an einem Zimmer einer ganz vermieteten Wohnung auch nicht – der Weg ist: erst umschalten, dann Zimmer anlegen, dann Mieter.",
        quelle: ["backend/vermietung.py:15-17", "frontend/src/pages/Tenants.jsx:358-361"],
    },
    {
        id: "mieter-status",
        text: "Der Status allein genügt nicht: Ein Mietende in der Vergangenheit beendet das Mietverhältnis, auch wenn niemand den Status nachgezogen hat. Sind Personen erfasst, braucht ein Mietverhältnis mit Status „aktiv“ mindestens einen Vertragspartner, der nicht ausgeschieden ist.",
        quelle: ["frontend/src/lib/format.js:161-162", "backend/personen.py:236-241"],
    },
    {
        id: "name-im-vertrag",
        text: "Der Name im Vertrag benennt, wer den Vertrag trägt: alle aktiven Vertragspartner, mit „ / “ verbunden – Ausgeschiedene und Bewohner stehen nicht darin. Er folgt der Personenliste, solange niemand etwas anderes einträgt; danach gilt der eingetragene Name („Fam. Passenheim“ neben „Anna / Jan Passenheim“).",
        quelle: ["backend/personen.py:350-355", "frontend/src/pages/Tenants.jsx:70-73"],
    },
    {
        id: "namensfeld",
        text: "Ohne Personenliste gilt das Namensfeld – auch im Zahlungsabgleich, der einen Einzahler dagegen prüft. Beim Erfassen als Personen wird nichts zerlegt: Es entsteht eine Zeile mit dem ganzen Namen, aufgeteilt wird von Hand.",
        quelle: ["backend/server.py:755-756", "backend/personen.py:181-186", "frontend/src/pages/Tenants.jsx:476-477"],
    },
    {
        id: "nicht-zugewiesen",
        text: "Eingänge mit freiem Rest auf Konten, die auf Einheit, Immobilie oder Entität dieses Mietverhältnisses zeigen. Ein Einheitenkonto gehört dieser Wohnung; von einem Entitätskonto wird nur aufgenommen, was den Namen des Mieters im Text oder beim Einzahler trägt. Ohne diese Liste sähe ein Mieter säumig aus, dessen Zahlung nur nicht zugeordnet ist.",
        quelle: ["backend/mietkonto.py:992-999", "frontend/src/components/MieterKontoDialog.jsx:344-347"],
    },
    {
        id: "nk-vorauszahlung",
        text: "Die Vorauszahlung steht am Mietverhältnis, nicht an der Einheit: Ohne Mieter bleibt sie leer. Bei Pauschalmiete gibt es keine getrennte Vorauszahlung – sie steckt drin.",
        quelle: "frontend/src/pages/Units.jsx:882-884",
    },
    {
        id: "person-bis",
        text: "Gesetzt heißt: aus dem Vertrag ausgeschieden – der Vertrag läuft weiter (Teilauszug, kein Mieterwechsel). Gespeicherte Personen werden nicht gelöscht, sie scheiden über dieses Datum aus; das ist Vertragsgeschichte.",
        quelle: ["backend/server.py:733-734", "backend/server.py:741", "frontend/src/pages/Tenants.jsx:65-67"],
    },
    {
        id: "person-name",
        text: "Der Name, wie unterschrieben. Der Zahlungsabgleich prüft einen Einzahler gegen die Namen aller Personen – alle Rollen, auch Ausgeschiedene: Eine Zahlung aus der Zeit vor dem Teilauszug muss weiter treffen.",
        quelle: ["backend/server.py:736", "backend/personen.py:183-185"],
    },
    {
        id: "person-rolle",
        text: "Vertragspartner tragen den Vertrag und stehen im Namen des Vertrags; ein Bewohner wohnt, haftet aber nicht – überweisen darf auch er. Ein Mietverhältnis mit Status „aktiv“ braucht mindestens einen Vertragspartner, der nicht ausgeschieden ist.",
        quelle: ["backend/server.py:737", "backend/personen.py:350-355", "backend/personen.py:371-372", "backend/personen.py:236-237"],
    },
    {
        id: "person-von",
        text: "Seit wann die Person im Vertrag steht. Leer heißt: seit Vertragsbeginn.",
        quelle: "backend/server.py:740",
    },
    {
        id: "personen-im-vertrag",
        text: "Ein gemeinsamer Vertrag bleibt ein Mietverhältnis – ein Soll, eine Kaution, ein Mietende; die Mieter haften gesamtschuldnerisch. Die Personen sind Zeilen darin.",
        quelle: ["backend/server.py:731-733", "frontend/src/pages/Tenants.jsx:40-42"],
    },
    {
        id: "saeumig",
        text: "Säumig ist, was fällig und unbezahlt ist: der offene Rest der roten Positionen, deren Fälligkeit erreicht ist – deckt ein Vorschlag eine Position teilweise, der Rest danach. Noch nicht fällige Positionen sind kein Rückstand.",
        quelle: ["backend/mietkonto.py:1067-1068", "backend/mietkonto.py:882-885"],
    },
    {
        id: "schon-vorhanden",
        text: "Jede Buchung trägt Banking4s eigene Objektnummer als Schlüssel. Derselbe Export zweimal eingelesen legt nichts doppelt an, ein überlappender Zeitraum ergänzt nur das Fehlende.",
        quelle: ["backend/bank_import.py:19-21", "backend/mietkonto.py:3850-3852"],
    },
    {
        id: "separat-vermietet",
        text: "Ob Vertrag und Abrechnung separat laufen oder zusammen mit der übergeordneten Wohnung.",
        quelle: "backend/server.py:666",
    },
    {
        id: "soll-position",
        text: "Die Soll-Position ist die Forderung eines Mietverhältnisses für einen Monat – gespeichert, nicht berechnet. Würde man das Soll aus der aktuellen Miete ableiten, schriebe eine Mieterhöhung die Vergangenheit um; der eingefrorene Betrag ist der Grund, warum es die Position gibt.",
        quelle: "backend/mietkonto.py:10-15",
    },
    {
        id: "uebergabe",
        text: "Die Übergabe ist die Schlüsselrückgabe – ein zweites Datum neben dem Mietende, das selten auf denselben Tag fällt. Sie beendet das Soll nicht, sie beginnt die Abnahme; die Angabe ist optional.",
        quelle: ["backend/server.py:792-796", "backend/server.py:1326-1328"],
    },
    {
        id: "uebergeordnete-einheit",
        text: "Die Wohnung, zu der Sondernutzungsrecht, Zubehör oder Zimmer gehören; bei Gesamtobjekt bleibt sie leer.",
        quelle: ["backend/server.py:667", "frontend/src/pages/Units.jsx:561-562"],
    },
    {
        id: "vermietungsart",
        text: "Die Vermietungsart ist ein Zustand, keine Erlaubnis: An einer Einzelzimmer-Wohnung hängt kein eigener Mieter, an einem Zimmer einer ganz vermieteten Wohnung auch nicht, und umgeschaltet wird nur, wenn nichts mehr läuft. Die Zimmer sind eigene Mieteinheiten an der Wohnung – jedes mit genau einem Mieter, einem Soll, einer Kaution.",
        quelle: ["backend/vermietung.py:8-10", "backend/vermietung.py:13-18"],
    },
    {
        id: "vorschlag",
        text: "Ein Vorschlag kommt vom Zuordnungsautomaten, „bestätigt“ von einem Menschen. Nur bestätigte Zuordnungen zählen auf den Saldo; ein Vorschlag färbt die Ampel nie grün, verhindert aber Rot: Liegt die passende Zahlung nachweislich auf dem Konto, ist die Position kein Rückstand, sondern eine Vorlage zur Durchsicht.",
        quelle: ["backend/mietkonto.py:231-233", "backend/mietkonto.py:581-584"],
    },
    {
        id: "zeilen-gelesen",
        text: "Übersprungene Zeilen werden gezählt und benannt, damit niemand sie für verloren hält – ein Importer, der stillschweigend Zeilen verliert, ist schlimmer als keiner. Nullbuchungen sind Hinweise, keine Zahlungen.",
        quelle: ["backend/bank_import.py:189-192", "backend/bank_import.py:231-234"],
    },
    {
        id: "zuordnung",
        text: "Eine Zuordnung verbindet eine Zahlung mit einer Soll-Position, mit Betrag. Eine Zahlung kann mehrere Monate abdecken (jemand zahlt nach), und ein Monat kann aus mehreren Zahlungen bestehen (Ratenzahlung, Teilzahlung). „Gedeckt aus 2 Überweisungen“ zählt deshalb verschiedene Überweisungen, nicht Zuordnungszeilen.",
        quelle: ["backend/mietkonto.py:21-26", "frontend/src/components/MieterKontoDialog.jsx:54-56"],
    },
    {
        id: "aktion.createAllocation",
        text: "Zahlung einer Soll-Position zuordnen. Ohne Betrag wird so viel zugeordnet, wie die Position noch braucht und die Zahlung noch hergibt; es wird nie mehr verteilt als vorhanden.",
        quelle: "backend/mietkonto.py:1514-1519",
    },
    {
        id: "aktion.setKlaerung",
        text: "Grund für eine Abweichung setzen oder entfernen. Ein Grund ist Pflicht: Eine Klärung ohne Begründung erklärt nichts und macht die Ampel nur unbrauchbar.",
        quelle: "backend/mietkonto.py:1202-1207",
    },
    {
        id: "aktion.lifecycle",
        text: "Öffnet den Wechsel von Strategie (HOLD / TRADE / HOLD_THEN_SELL), Status und Exit-Datum, mit der Historie der Wechsel und dem Löschen fehlerhafter Einträge.",
        quelle: "frontend/src/components/LifecycleBadge.jsx:14-18",
    },
];

// Pfad ab Repo-Wurzel, optional :zeile oder :von-bis. Kein freier Satz.
const FUNDSTELLE = /^[\w.-]+(\/[\w.-]+)*\.[A-Za-z0-9]+(:\d+(-\d+)?)?$/;

export const istSeite = (id) => String(id).startsWith("seite.");

export const quellenListe = (quelle) => (Array.isArray(quelle) ? quelle : [quelle]);

/** Alle Formfehler einer Eintragsliste, als Sätze. Leer heißt: in Ordnung. */
export function registerFehler(liste) {
    const fehler = [];
    const gesehen = new Set();
    liste.forEach((e, i) => {
        const wo = `Eintrag ${i + 1}${e && e.id ? ` "${e.id}"` : ""}`;
        if (!e || typeof e.id !== "string" || !e.id.trim()) {
            fehler.push(`${wo}: id fehlt`);
            return;
        }
        if (gesehen.has(e.id)) fehler.push(`${wo}: id doppelt`);
        gesehen.add(e.id);
        if (istSeite(e.id)) {
            const t = e.text || {};
            for (const k of ["frage", "tun", "nicht"]) {
                if (typeof t[k] !== "string" || !t[k].trim()) fehler.push(`${wo}: Seitenkopf ohne „${k}“`);
            }
        } else if (typeof e.text !== "string" || !e.text.trim()) {
            fehler.push(`${wo}: text fehlt`);
        }
        const q = e.quelle == null ? [] : quellenListe(e.quelle);
        if (!q.length) fehler.push(`${wo}: quelle fehlt`);
        q.forEach((s) => {
            if (typeof s !== "string" || !FUNDSTELLE.test(s)) {
                fehler.push(`${wo}: quelle „${s}“ ist keine Fundstelle (Pfad[:zeile[-zeile]])`);
            }
        });
    });
    return fehler;
}

const produktion = () => typeof process !== "undefined" && process.env.NODE_ENV === "production";

/**
 * Prüft die Liste und gibt die gültigen Einträge zurück. Außerhalb der
 * Produktion wirft ein Fehler -- in Entwicklung und Tests soll er sofort
 * auffallen. In Produktion fällt nur der kaputte Eintrag weg; der eigentliche
 * Wächter ist ohnehin das Prüfskript vor dem Bau.
 */
export function pruefeEintraege(liste, { werfen = !produktion() } = {}) {
    const fehler = registerFehler(liste);
    if (!fehler.length) return liste;
    if (werfen) throw new Error(`Hilfe-Register fehlerhaft:\n${fehler.join("\n")}`);
    console.error(`Hilfe-Register fehlerhaft:\n${fehler.join("\n")}`);
    const ids = new Set();
    return liste.filter((e) => {
        if (registerFehler([e]).length || ids.has(e.id)) return false;
        ids.add(e.id);
        return true;
    });
}

export const EINTRAEGE_ROH = EINTRAEGE;
const GUELTIG = pruefeEintraege(EINTRAEGE);
export const REGISTER = Object.fromEntries(GUELTIG.map((e) => [e.id, e]));
export const HILFE_IDS = GUELTIG.map((e) => e.id);

/**
 * Der Eintrag zu einer id. Unbekannt: wirft außerhalb der Produktion, in
 * Produktion null plus console.error -- die Seite bleibt heil.
 */
export function eintrag(id, { werfen = !produktion() } = {}) {
    const e = REGISTER[id];
    if (e) return e;
    const meldung = `Hilfe-Register: unbekannte id „${id}“`;
    if (werfen) throw new Error(meldung);
    console.error(meldung);
    return null;
}
